import React, { useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import StateLabel from './StateLabel';
import MessageBody from './MessageBody';
import HolderControls from './HolderControls';
import DraftSuggestion from './DraftSuggestion';
import { exactWhen } from '../../../Utils/conversationAge';
import {
  ACCOUNT_UNKNOWN_REASONS, channelAccountLabel, reasonLabel,
} from '../../../Services/EmailInboxService';

/**
 * THE CENTRE PANE: one conversation, NEWEST MESSAGE FIRST, and the decision
 * waiting on it.
 *
 * INBOUND AND OUTBOUND ARE VISUALLY DISTINCT — ours is indented and tinted.
 * `direction` is the one thing the archive promises every channel will know,
 * so this is the part of the layout that survives WhatsApp arriving: a second
 * producer fills the same field and these rows render unchanged.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * NEWEST FIRST, IN BOTH LISTS, AND THE DRAFT MOVED WITH THEM.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * "Can we change the order in the view, so newest messages first" — and this
 * pane was the half that disagreed with the other. The queue has always come
 * back newest-first (the API's own `newestFirst` over conversations); the
 * archive serves ONE conversation oldest-first, "the order it happened", and
 * this pane used to render it in that order. So a reader scanning the queue
 * top-down was reading time one way and then, on clicking, the other way.
 * One direction now: newest at the top, everywhere on this screen.
 *
 * REVERSED HERE AND NOT IN `EmailInboxService`. The service's order is
 * load-bearing for a second consumer: `conversationBookingReference` walks
 * `conversation.messages` and takes the FIRST reference it finds precisely
 * because the oldest message wins — "a long thread can come to mention a
 * second booking later and the one it is ABOUT is the one it started on".
 * Reversing at the source would silently re-point the context pane's booking
 * lookup at the newest reference instead. Display order is a display concern.
 *
 * THE DRAFT IS THE FIRST THING IN THE TIMELINE NOW, and that is the same rule
 * as before rather than a new one: THE DECISION SITS AGAINST THE MESSAGE IT
 * ANSWERS. Oldest-first put the newest message last and the draft after it;
 * newest-first puts the newest message first and the draft before it. The two
 * are still adjacent — they have simply moved, together, to the end of the
 * pane the eye starts at. Left at the bottom the draft would have sat under
 * the OLDEST message, answering a question from weeks ago, and a reviewer
 * would have had to scroll the whole history to reach the one control the
 * screen exists for.
 *
 * THE NEWEST MESSAGE IS ALSO THE OPEN ONE, which it has been since this pane
 * was built: every message used to be a closed preview with its own toggle, so
 * reading a twelve-message thread was twelve clicks and twelve requests.
 * Bodies are still fetched on demand, one per message, so opening one newest
 * message is one request rather than twelve. Auto-opening fixed WHICH message
 * is readable; the order fixes WHERE it is.
 *
 * `onResolve` SERVES BOTH the holder controls and the draft card, because all
 * five of their buttons are one decision in the server's vocabulary — which of
 * the four `DRAFT_RESOLUTIONS` this conversation came to.
 * A reviewer cannot judge a reply without the message beside it, which is why
 * it is not a screen of its own. Nothing in this pane sends anything; see
 * DraftSuggestion.
 */
function AccountLine({ conversation }) {
  const { account, account_unknown_reason: unknownReason } = conversation;
  const label = channelAccountLabel(conversation);

  if (account) return <span className="cs-thread__account">{label}</span>;

  // Not blank, and not a default: "we could not tell which mailbox" is itself
  // a fact, and it is every conversation older than 2026-10-12.
  return (
    <span className="cs-thread__account cs-thread__account--unknown">
      {`${label} — `}
      {reasonLabel(ACCOUNT_UNKNOWN_REASONS, unknownReason) || 'no reason given'}
    </span>
  );
}

function Message({ message, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const outbound = message.direction === 'outbound';

  return (
    <div
      className={`cs-msg ${outbound ? 'cs-msg--out' : 'cs-msg--in'}`}
      data-testid={`cs-message-${message.direction}`}
    >
      <div className="cs-msg__meta">
        <span className="cs-msg__who">{message.sender}</span>
        <span className="cs-msg__when" title={exactWhen(message.sent_at)}>
          {exactWhen(message.sent_at)}
        </span>
      </div>
      {message.email?.subject && (
        <div className="cs-msg__subject">{message.email.subject}</div>
      )}
      {!open && <div className="cs-msg__preview">{message.preview}</div>}
      <button
        type="button"
        className="cs-msg__toggle"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        {open ? 'Hide full message' : 'Show full message'}
      </button>
      {open && <MessageBody messageId={message.id} />}
    </div>
  );
}

function ConversationThread({
  conversation, state, entry, draft, run, loading, error, busy, deskError,
  onClaim, onRelease, onResolve,
}) {
  if (loading) {
    return (
      <div className="cs-thread__empty">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading conversation
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger" className="m-3">{error}</Alert>;
  }

  if (!conversation) {
    return (
      <div className="cs-thread__empty text-muted">
        <i className="fa fa-envelope-open-text d-block mb-2" />
        Pick a conversation on the left
      </div>
    );
  }

  // The archive serves these OLDEST FIRST. The newest is therefore the last
  // one, and it is identified before the copy is reversed so the two readings
  // cannot drift apart.
  const messages = conversation.messages || [];
  const newestId = messages.length ? messages[messages.length - 1].id : null;
  const newestFirst = [...messages].reverse();

  return (
    <div className="cs-thread">
      <div className="cs-thread__head">
        <h5 className="cs-thread__title">{conversation.title || 'No subject'}</h5>
        <div className="cs-thread__sub">
          <AccountLine conversation={conversation} />
          <StateLabel descriptor={state} />
        </div>
      </div>

      <HolderControls
        state={state}
        entry={entry}
        busy={busy}
        onClaim={onClaim}
        onRelease={onRelease}
        onResolve={onResolve}
      />

      {deskError && (
        <Alert variant="warning" className="py-1 px-2 small mb-2">{deskError}</Alert>
      )}

      <div className="cs-thread__messages">
        {/* FIRST in the timeline, against the newest message, because that is
            where the decision belongs — see the header on why "first" and
            "last" are the same rule read in two orders. It renders a failed
            run too: a missing suggestion must not read as a suggestion that
            was not needed. See DraftSuggestion. */}
        <DraftSuggestion
          /* KEYED ON THE RUN so a half-typed edit cannot survive into
             another conversation: the card holds the editor's text in its own
             state, and without this the next conversation would open showing
             the previous one's draft mid-edit. The run id is what identifies a
             draft on the record — there is no id of its own, because a draft
             is an `ai_decisions` row of its run. */
          key={draft?.run_id || run?.run_id || 'no-draft'}
          draft={draft}
          run={run}
          canAct={state.value === 'mine'}
          busy={busy}
          onDecide={onResolve}
        />

        {newestFirst.map((message) => (
          <Message
            key={message.id}
            message={message}
            defaultOpen={message.id === newestId}
          />
        ))}

        {/* AT THE FOOT, which is where the missing messages would be now that
            the list runs newest-first. Above the newest message it would have
            read as a caveat about what follows it. */}
        {conversation.truncated && (
          <div className="cs-thread__truncated">
            Older messages in this conversation exist but are not on this page —
            the newest are the ones shown.
          </div>
        )}
      </div>
    </div>
  );
}

export default ConversationThread;
